import React, { useState, useMemo, useCallback } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  DragIndicator as DragIcon,
  ViewModule as GridIcon,
  MoreVert as OptionsIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface GridLayoutViewProps {
  widgets: any[];
  isLoading: boolean;
  renderWidget: (widget: any) => React.ReactNode;
  onLayoutSave?: (
    widgets: Array<{ id: string; position: any }>,
  ) => Promise<void>;
  onAddWidget?: () => void;
  onPageSettings?: () => void;
  onEditWidget?: (widget: any) => void;
  onDeleteWidget?: (widget: any) => void;
}

interface WidgetWrapperProps {
  widget: any;
  isEditMode: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  renderWidget: (widget: any) => React.ReactNode;
}

const WidgetWrapper: React.FC<WidgetWrapperProps> = ({
  widget,
  isEditMode,
  onEdit,
  onDelete,
  renderWidget,
}) => {
  return (
    <Paper
      elevation={isEditMode ? 4 : 1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        border: isEditMode ? '2px solid' : '1px solid',
        borderColor: isEditMode ? 'primary.main' : 'divider',
        '&:hover': isEditMode
          ? {
              boxShadow: 6,
              borderColor: 'primary.dark',
            }
          : {},
      }}
    >
      {isEditMode && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '40px',
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            zIndex: 10,
            '&:hover': {
              bgcolor: 'primary.dark',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              cursor: 'move',
            }}
            className="drag-handle"
          >
            <DragIcon fontSize="small" />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              {widget.configuration?.title || 'Widget'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onEdit && (
              <Tooltip title="Edit Widget">
                <IconButton
                  size="small"
                  sx={{
                    color: 'inherit',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onEdit();
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Widget">
                <IconButton
                  size="small"
                  sx={{
                    color: 'inherit',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.1)',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onDelete();
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          pt: isEditMode ? '40px' : 0,
        }}
      >
        {renderWidget(widget)}
      </Box>

      {isEditMode && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            width: 20,
            height: 20,
            cursor: 'se-resize',
            '&::after': {
              content: '"⤡"',
              position: 'absolute',
              bottom: 0,
              right: 0,
              color: 'primary.main',
              fontSize: '16px',
              lineHeight: 1,
            },
          }}
        />
      )}
    </Paper>
  );
};

export const GridLayoutView: React.FC<GridLayoutViewProps> = ({
  widgets,
  isLoading,
  renderWidget,
  onLayoutSave,
  onAddWidget,
  onPageSettings,
  onEditWidget,
  onDeleteWidget,
}) => {
  // Force webpack rebuild
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [tempLayout, setTempLayout] = useState<Layout[] | null>(null);

  // Convert widgets to react-grid-layout format
  const currentLayout = useMemo(() => {
    return widgets.map((widget) => ({
      i: widget.id,
      x: widget.position?.x || 0,
      y: widget.position?.y || 0,
      w: widget.position?.width || 4,
      h: widget.position?.height || 3,
      minW: 2,
      minH: 2,
      maxW: 12,
    }));
  }, [widgets]);

  const handleLayoutChange = useCallback(
    (layout: Layout[]) => {
      if (isEditMode) {
        setTempLayout(layout);
      }
    },
    [isEditMode],
  );

  const handleEditClick = () => {
    setTempLayout(currentLayout);
    setIsEditMode(true);
  };

  const handleSave = async () => {
    if (!tempLayout || !onLayoutSave) return;

    setIsSaving(true);
    try {
      const updates = tempLayout.map((item) => ({
        id: item.i,
        position: {
          x: item.x,
          y: item.y,
          width: item.w,
          height: item.h,
        },
      }));

      await onLayoutSave(updates);
      setIsEditMode(false);
      setTempLayout(null);
    } catch (error) {
      console.error('Failed to save layout:', error);
      // Could add error toast here
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setTempLayout(null);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Edit Mode Toolbar - Always visible */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {!isEditMode ? (
          <>
            {onAddWidget && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onAddWidget}
                size="small"
              >
                Add Widget
              </Button>
            )}
            <Tooltip title="Enable drag & drop editing">
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditClick}
                size="small"
              >
                Edit Layout
              </Button>
            </Tooltip>
            {onPageSettings && (
              <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={onPageSettings}
                size="small"
              >
                Settings
              </Button>
            )}
          </>
        ) : (
          <>
            <Typography
              variant="caption"
              color="primary.main"
              sx={{ mr: 'auto' }}
            >
              💡 Drag widgets to reposition | Drag corners to resize
            </Typography>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
              size="small"
            >
              {isSaving ? 'Saving...' : 'Save Layout'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={isSaving}
              size="small"
            >
              Cancel
            </Button>
          </>
        )}
      </Box>

      {/* Grid Layout */}
      <ResponsiveGridLayout
        className="layout"
        layouts={{
          lg: tempLayout || currentLayout,
          md: tempLayout || currentLayout,
          sm: tempLayout || currentLayout,
          xs: tempLayout || currentLayout,
        }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
        rowHeight={60}
        margin={[15, 15]}
        isDraggable={isEditMode}
        isResizable={isEditMode}
        onLayoutChange={handleLayoutChange}
        compactType="vertical"
        preventCollision={false}
        useCSSTransforms={true}
        draggableHandle=".drag-handle"
        style={{
          minHeight: widgets && widgets.length > 0 ? '400px' : '0px',
        }}
      >
        {widgets.map((widget) => (
          <div key={widget.id}>
            <WidgetWrapper
              widget={widget}
              isEditMode={isEditMode}
              renderWidget={renderWidget}
              onEdit={onEditWidget ? () => onEditWidget(widget) : undefined}
              onDelete={
                onDeleteWidget ? () => onDeleteWidget(widget) : undefined
              }
            />
          </div>
        ))}
      </ResponsiveGridLayout>

      {/* Empty State - Show when no widgets */}
      {(!widgets || widgets.length === 0) && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          textAlign="center"
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'grey.50',
          }}
        >
          <GridIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No Widgets Yet
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Click the "Add Widget" button above to get started
          </Typography>
        </Box>
      )}

      {/* Edit Mode Instructions */}
      {isEditMode && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'info.lighter',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'info.light',
          }}
        >
          <Typography variant="body2" color="info.dark">
            <strong>Edit Mode:</strong> Drag widgets by their header to move
            them. Drag the corner icon (⤡) to resize. Click{' '}
            <strong>Save Layout</strong> when you're done, or{' '}
            <strong>Cancel</strong> to discard changes.
          </Typography>
        </Box>
      )}
    </Box>
  );
};
